import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from './useAuth0';
import { useProgressCalculation } from './useProgressCalculation';
import { useCurrentSession } from './useCurrentSession';
import { intakePortalApi } from '@/utils/api';
import { applySalesOpsOverrides } from '@/config/salesOpsFieldOverrides';

export const useNavigationState = () => {
  const { isAuthenticated } = useAuth0();
  const { currentSessionId } = useCurrentSession();

  const { data: sessionState, isLoading: isLoadingState } = useQuery({
    queryKey: ['session-state', currentSessionId],
    queryFn: async () => {
      if (!currentSessionId) throw new Error('No session ID');
      const response = await intakePortalApi.getState(currentSessionId);
      return response;
    },
    enabled: isAuthenticated && !!currentSessionId,
  });

  const { data: rawSchema, isLoading: isLoadingSchema } = useQuery({
    queryKey: ['session-schema', currentSessionId],
    queryFn: async () => {
      if (!currentSessionId) throw new Error('No session ID');
      const response = await intakePortalApi.getSchema(currentSessionId);
      return response;
    },
    enabled: isAuthenticated && !!currentSessionId,
  });

  // Apply same overrides as Search page so progress calculation matches
  const schema = useMemo(() => {
    return rawSchema ? applySalesOpsOverrides(rawSchema) : undefined;
  }, [rawSchema]);

  const progress = useProgressCalculation(sessionState, schema);
  
  const isLoading = isLoadingState || isLoadingSchema;
  
  return {
    progress,
    isLoading,
    welcomeEnabled: true,
    searchEnabled: progress.welcome === 'completed',
    confirmEnabled: progress.search === 'completed',
    submitEnabled: progress.confirm === 'completed',
    charIncomeEnabled: sessionState?.product_led_growth 
      ? progress.plg_submit === 'completed'  // PLG flow: after PLG submit
      : progress.submit === 'completed',     // Non-PLG flow: after community input submit
    charSection8Enabled: progress.char_income === 'completed',
    charNewConstructionEnabled: progress.char_section8 === 'completed',
    charStudentEnabled: progress.char_newconstruction === 'completed',
    charSharedOpsEnabled: progress.char_student === 'completed',
    charSubmitForReviewEnabled: progress.char_sharedops !== 'pending',
    // Implementation Contacts gating: unlock after char_submit (and plg_submit if PLG enabled)
    implContactsEnabled: sessionState?.product_led_growth 
      ? (progress.plg_submit === 'completed' && progress.char_submit !== 'pending')  // PLG flow: after BOTH PLG and characteristics
      : progress.char_submit !== 'pending', // Non-PLG flow: after characteristics submit
    implProjectLeadEnabled: progress.char_submit !== 'pending',
    implTechnicalEnabled: progress.impl_project_lead !== 'pending',
    implVoiceEnabled: progress.impl_technical !== 'pending',
    implMarketingEnabled: progress.impl_voice !== 'pending',
    implKnowledgeEnabled: progress.impl_marketing !== 'pending',
    implTrainingEnabled: progress.impl_knowledge !== 'pending',
    implOwnerEnabled: progress.impl_training !== 'pending',
    implSubmitEnabled: progress.impl_owner !== 'pending',
    // PLG section visibility: show section when product_led_growth is enabled (regardless of submit status)
    plgSectionVisible: !!sessionState?.product_led_growth,
    // PLG gating: enabled when product_led_growth is enabled AND submit is completed
    plgEnabled: !!sessionState?.product_led_growth && progress.submit === 'completed',
    // Keep page gating based on PLG progress, but not tied to Submit
    plgNameEnabled: !!sessionState?.product_led_growth && progress.submit === 'completed',
    plgAINameEnabled: !!sessionState?.product_led_growth && progress.submit === 'completed' && progress.plg_name !== 'pending',
    plgVoiceAreaEnabled: !!sessionState?.product_led_growth && progress.submit === 'completed' && progress.plg_ai_name !== 'pending',
    plgScheduleEnabled: !!sessionState?.product_led_growth && progress.submit === 'completed' && progress.plg_voice_area !== 'pending',
    plgSubmitEnabled: !!sessionState?.product_led_growth && progress.submit === 'completed' && progress.plg_schedule !== 'pending',
  };
};